import CorvuDialog from '@corvu/dialog'
import { native } from '@forest/native-services'
import { Fit, Layout, Rive, RiveFile } from '@rive-app/canvas-lite'
import { createSignal, onCleanup, onMount } from 'solid-js'
import { Motion } from 'solid-motionone'
import anim from '~/assets/anim/subscribed.riv?url'
import bgImg from '~/assets/images/bg.svg'
import { m } from '~/paraglide/messages'

/**
 * The purchase success view the other paywalls show, which Plus regained
 * (GLOSSARY.md) ends on. Its animation starts loading on mount, and the view
 * opens only once that settles, loaded or not; Start Now closes the page.
 * China Android has no plans here, so its copy is left out.
 */
export function SubscribedView(props: { open: boolean }) {
  const riveFile = new RiveFile({ src: anim })

  const loadingRiveFile = (async () => {
    await riveFile.init()
    return riveFile
  })()

  const [isAnimSettled, setIsAnimSettled] = createSignal(false)
  const [isBtnRevealed, setIsBtnRevealed] = createSignal(false)

  // Opens whether or not the animation loads: the purchase went through.
  const settleAnim = () => setIsAnimSettled(true)
  void loadingRiveFile.then(settleAnim, settleAnim)

  return (
    <CorvuDialog
      open={props.open && isAnimSettled()}
      // Start Now is the only way out.
      closeOnEscapeKeyDown={false}
      closeOnOutsidePointer={false}
      closeOnOutsideFocus={false}
    >
      <CorvuDialog.Portal>
        <CorvuDialog.Content class="fixed inset-0 z-50">
          <Motion.div
            class="size-full bg-[#001721] px-8 pt-[calc(60px+env(safe-area-inset-top))] text-center text-[#F3FEFF]"
            style={{
              'background-image': `url(${bgImg})`,
              'background-repeat': 'no-repeat',
              'background-size': 'cover',
              'background-position': 'center',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div class="h-[30vh] w-full">
              <Anim loadingRiveFile={loadingRiveFile} />
            </div>
            <Motion.div
              class="mt-6 text-[36px] font-bold"
              style={{
                'text-shadow': '0 0 10px rgba(255, 255, 255, 0.5)',
              }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 3,
                opacity: {
                  easing: [0.33, 1, 0.68, 1],
                  duration: 0.6,
                },
                y: {
                  easing: [0.5, 1, 0.89, 1],
                  duration: 0.6,
                },
              }}
            >
              <CorvuDialog.Label>
                {m.onboarding_paywall_subscribed_success_title()}
              </CorvuDialog.Label>
            </Motion.div>
            <Motion.div
              class="mt-2 flex h-[112px] items-center justify-center text-[20px] font-bold"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 3.5,
                opacity: {
                  easing: [0.33, 1, 0.68, 1],
                  duration: 0.6,
                },
                y: {
                  easing: [0.5, 1, 0.89, 1],
                  duration: 0.6,
                },
              }}
            >
              <CorvuDialog.Description>
                {m.onboarding_paywall_subscribed_success_content()}
              </CorvuDialog.Description>
            </Motion.div>
          </Motion.div>

          <Motion.div
            class="absolute bottom-12 left-0 right-0 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              delay: 4.5,
              easing: [0.33, 1, 0.68, 1],
              duration: 0.3,
            }}
            onMotionComplete={() => setIsBtnRevealed(true)}
          >
            <button
              type="button"
              class="group h-[52px] w-full rounded-full p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
              disabled={!isBtnRevealed()}
              onClick={() => void native.general.close()}
            >
              <span class="flex h-full w-full items-center justify-center rounded-full bg-plus-identity font-bold text-[#176361] group-active:bg-plus-pressed">
                {m.onboarding_paywall_subscribed_success_btn()}
              </span>
            </button>
          </Motion.div>
        </CorvuDialog.Content>
      </CorvuDialog.Portal>
    </CorvuDialog>
  )
}

function Anim(props: { loadingRiveFile: Promise<RiveFile> }) {
  let canvas!: HTMLCanvasElement // oxlint-disable-line eslint/no-unassigned-vars
  let rive: Rive | null
  const handleResize = () => {
    rive?.resizeDrawingSurfaceToCanvas()
  }
  onMount(() => {
    void props.loadingRiveFile.then(
      (riveFile) => {
        rive = new Rive({
          riveFile,
          artboard: 'paywall_success',
          stateMachines: ['State Machine 1'],
          canvas,
          autoplay: true,
          layout: new Layout({ fit: Fit.FitHeight }),
          onLoad: () => {
            rive?.resizeDrawingSurfaceToCanvas()
            window.addEventListener('resize', handleResize)
          },
        })
      },
      // Failing to load leaves the canvas empty; the view still shows.
      () => {},
    )
  })
  onCleanup(() => {
    rive?.stopRendering()
    rive?.cleanupInstances()
    rive = null
    window.removeEventListener('resize', handleResize)
  })
  return <canvas ref={canvas} class="size-full" />
}
